/**
 * <custom-select> — restyles a native <select> per Figma (node 2366:28140 closed
 * trigger, node 2366:28152 open listbox) while keeping the original <select>
 * as the sole source of truth for value/name/form — VariantSelects and
 * ProductOptionDropdowns keep working unchanged against it.
 *
 * Markup contract: <custom-select class="select"><select class="select__select">…
 * options…</select><span class="svg-wrapper">…</span></custom-select>. The
 * trigger button + listbox are injected at runtime, right after the <select>.
 */
if (!customElements.get('custom-select')) {
  customElements.define(
    'custom-select',
    class CustomSelect extends HTMLElement {
      constructor() {
        super();
        this.select = this.querySelector('select');
      }

      connectedCallback() {
        if (!this.select || this.select.multiple || this.dataset.enhanced === 'true') return;
        this.dataset.enhanced = 'true';

        this.classList.add('select--custom');
        this.select.classList.add('visually-hidden');
        this.select.tabIndex = -1;

        const listboxId = `${this.select.id}-listbox`;

        this.trigger = document.createElement('button');
        this.trigger.type = 'button';
        this.trigger.id = `${this.select.id}-trigger`;
        this.trigger.className = 'select__select custom-select__trigger';
        this.trigger.setAttribute('aria-haspopup', 'listbox');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('aria-controls', listboxId);

        this.triggerLabel = document.createElement('span');
        this.triggerLabel.className = 'custom-select__trigger-label';
        this.trigger.appendChild(this.triggerLabel);

        this.listbox = document.createElement('ul');
        this.listbox.id = listboxId;
        this.listbox.className = 'custom-select__listbox';
        this.listbox.setAttribute('role', 'listbox');
        this.listbox.hidden = true;

        this.select.insertAdjacentElement('afterend', this.listbox);
        this.select.insertAdjacentElement('afterend', this.trigger);

        const label = document.querySelector(`label[for="${this.select.id}"]`);
        if (label) label.setAttribute('for', this.trigger.id);

        this.buildOptions();
        this.syncTriggerLabel();

        this.trigger.addEventListener('click', () => this.toggle());
        this.trigger.addEventListener('keydown', this.onTriggerKeydown.bind(this));
        this.listbox.addEventListener('click', this.onOptionClick.bind(this));
        this.select.addEventListener('change', this.onNativeChange.bind(this));
        this.onDocumentClick = this.onDocumentClick.bind(this);
        document.addEventListener('click', this.onDocumentClick);

        this.typeaheadBuffer = '';
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      buildOptions() {
        this.listbox.innerHTML = '';

        this.options = Array.from(this.select.options).map((option, index) => {
          const item = document.createElement('li');
          item.id = `${this.listbox.id}-option-${index}`;
          item.setAttribute('role', 'option');
          item.dataset.index = index;
          item.textContent = option.textContent.trim();
          item.className = 'custom-select__option';

          if (option.disabled) {
            item.classList.add('custom-select__option--disabled');
            item.setAttribute('aria-disabled', 'true');
          }
          if (option.dataset.optionUnavailable === 'true') {
            item.classList.add('custom-select__option--unavailable');
          }

          this.listbox.appendChild(item);
          return item;
        });

        this.syncActiveState();
      }

      syncActiveState() {
        this.options.forEach((item, index) => {
          const selected = index === this.select.selectedIndex;
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
          item.classList.toggle('custom-select__option--active', selected);
        });
      }

      syncTriggerLabel() {
        const selectedOption = this.select.selectedOptions[0];
        this.triggerLabel.textContent = selectedOption ? selectedOption.textContent.trim() : '';
        // The "Select" placeholder has its own subdued style.
        this.triggerLabel.classList.toggle(
          'custom-select__trigger-label--placeholder',
          selectedOption?.dataset.placeholder !== undefined
        );
      }

      onNativeChange() {
        this.syncActiveState();
        this.syncTriggerLabel();
      }

      toggle() {
        if (this.listbox.hidden) this.open();
        else this.close();
      }

      open() {
        if (!this.listbox.hidden) return;
        this.listbox.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        this.setActive(this.select.selectedIndex);
      }

      close() {
        if (this.listbox.hidden) return;
        this.listbox.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        this.trigger.removeAttribute('aria-activedescendant');
      }

      firstEnabledIndex() {
        return this.options.findIndex((item) => !item.classList.contains('custom-select__option--disabled'));
      }

      lastEnabledIndex() {
        for (let i = this.options.length - 1; i >= 0; i--) {
          if (!this.options[i].classList.contains('custom-select__option--disabled')) return i;
        }
        return -1;
      }

      setActive(index) {
        if (index < 0 || index >= this.options.length) return;
        this.activeIndex = index;
        this.options.forEach((item, i) => {
          item.classList.toggle('custom-select__option--focused', i === index);
        });
        this.trigger.setAttribute('aria-activedescendant', this.options[index].id);
        this.options[index].scrollIntoView({ block: 'nearest' });
      }

      moveActive(step) {
        let index = this.activeIndex;
        do {
          index += step;
        } while (
          index >= 0 &&
          index < this.options.length &&
          this.options[index].classList.contains('custom-select__option--disabled')
        );

        if (index >= 0 && index < this.options.length) this.setActive(index);
      }

      commit(index) {
        const item = this.options[index];
        if (!item || item.classList.contains('custom-select__option--disabled')) return;

        this.select.selectedIndex = index;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
        this.trigger.focus();
      }

      onOptionClick(event) {
        const item = event.target.closest('[role="option"]');
        if (!item) return;
        this.commit(Number(item.dataset.index));
      }

      onDocumentClick(event) {
        if (!this.listbox.hidden && !this.contains(event.target)) this.close();
      }

      onTriggerKeydown(event) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (this.listbox.hidden) this.open();
            else this.moveActive(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (this.listbox.hidden) this.open();
            else this.moveActive(-1);
            break;
          case 'Home':
            if (!this.listbox.hidden) {
              event.preventDefault();
              this.setActive(this.firstEnabledIndex());
            }
            break;
          case 'End':
            if (!this.listbox.hidden) {
              event.preventDefault();
              this.setActive(this.lastEnabledIndex());
            }
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (this.listbox.hidden) this.open();
            else this.commit(this.activeIndex);
            break;
          case 'Escape':
            if (!this.listbox.hidden) {
              event.preventDefault();
              this.close();
            }
            break;
          case 'Tab':
            this.close();
            break;
          default:
            if (event.key.length === 1) this.onTypeahead(event.key);
        }
      }

      onTypeahead(char) {
        clearTimeout(this.typeaheadTimeout);
        this.typeaheadBuffer += char.toLowerCase();
        this.typeaheadTimeout = setTimeout(() => {
          this.typeaheadBuffer = '';
        }, 600);

        const match = this.options.findIndex(
          (item) =>
            !item.classList.contains('custom-select__option--disabled') &&
            item.textContent.trim().toLowerCase().startsWith(this.typeaheadBuffer)
        );

        if (match === -1) return;
        if (this.listbox.hidden) this.commit(match);
        else this.setActive(match);
      }
    }
  );
}
